import { useParams } from "react-router-dom";
import * as translator from "../../utils/translator";

import wallet from '../../icons/wallet.svg';
import Klarna from '../../icons/klarna.svg';
import Visa from '../../icons/Visa.svg';
import Maestro from '../../icons/Maestro.svg';
import GPay from '../../icons/GPay.svg';
import ApplePay from '../../icons/ApplePay.svg';
import Wire from '../../icons/Wire.svg';
import PayPal from '../../icons/PayPal.svg';
import RevolutPay from '../../icons/RevolutPay.png';
import PayByBank from '../../icons/PayByBank.png';
import BtShort from '../../icons/bt-short.png';
import CartesBancaires from '../../icons/cartes-bancaires.png';
import Sepa from '../../icons/sepa.png';

type PaymentIcon = { code: string; title: string; iconUrl: string };
type StaticIcon = { src: string; alt: string };

const STATIC_ICONS: StaticIcon[] = [
  { src: Klarna, alt: 'Klarna' },
  { src: Visa, alt: 'Visa' },
  { src: Maestro, alt: 'Mastercard' },
  { src: GPay, alt: 'GPay' },
  { src: ApplePay, alt: 'ApplePay' },
  { src: Wire, alt: 'Wire' },
  { src: PayPal, alt: 'PayPal' },
];

/**
 * HL-11138: the UK banner mirrors the methods the GB payment_icon_login slot
 * serves, in the same `payment_types`.`order`, so a visitor with no cart sees
 * the same methods as one with a cart. Wire and PayPal are not among them.
 * The UK site is reachable as both /uk and /gb; other markets keep STATIC_ICONS.
 */
const UK_STATIC_ICONS: StaticIcon[] = [
  { src: Klarna, alt: 'Klarna' },
  { src: Visa, alt: 'Visa' },
  { src: Maestro, alt: 'Maestro' },
  { src: GPay, alt: 'Google Pay' },
  { src: ApplePay, alt: 'Apple Pay' },
  { src: RevolutPay, alt: 'Revolut Pay' },
  { src: PayByBank, alt: 'Pay By Bank' },
];

/** DE payment_icon_login slot, in `payment_types`.`order` (HL-12050: bank transfer shows the SEPA icon). */
const DE_STATIC_ICONS: StaticIcon[] = [
  { src: Klarna, alt: 'Klarna' },
  { src: Visa, alt: 'Visa' },
  { src: Maestro, alt: 'Maestro' },
  { src: GPay, alt: 'Google Pay' },
  { src: ApplePay, alt: 'Apple Pay' },
  { src: Sepa, alt: 'SEPA' },
  { src: PayPal, alt: 'PayPal' },
];

/** CH payment_icon_login slot, in `payment_types`.`order` (PayPal last, added after the PM review of the payment page). */
const CH_STATIC_ICONS: StaticIcon[] = [
  { src: Klarna, alt: 'Klarna' },
  { src: Visa, alt: 'Visa' },
  { src: Maestro, alt: 'Maestro' },
  { src: GPay, alt: 'Google Pay' },
  { src: ApplePay, alt: 'Apple Pay' },
  { src: Wire, alt: 'Wire' },
  { src: PayPal, alt: 'PayPal' },
];

/** FR payment_icon_login slot, in `payment_types`.`order` (Klarna first, added after the PM review of the payment page). */
const FR_STATIC_ICONS: StaticIcon[] = [
  { src: Klarna, alt: 'Klarna' },
  { src: Visa, alt: 'Visa' },
  { src: Maestro, alt: 'Maestro' },
  { src: BtShort, alt: 'Bank Transfer' },
  { src: GPay, alt: 'Google Pay' },
  { src: ApplePay, alt: 'Apple Pay' },
  { src: PayPal, alt: 'PayPal' },
  { src: CartesBancaires, alt: 'Cartes Bancaires' },
];

const STATIC_ICONS_BY_COUNTRY: Record<string, StaticIcon[]> = {
  gb: UK_STATIC_ICONS,
  uk: UK_STATIC_ICONS,
  de: DE_STATIC_ICONS,
  ch: CH_STATIC_ICONS,
  fr: FR_STATIC_ICONS,
};

interface AcceptedPaymentMethodsBannerProps {
  paymentIcons?: PaymentIcon[];
}

export default function AcceptedPaymentMethodsBanner({ paymentIcons }: AcceptedPaymentMethodsBannerProps) {
  const {country} = useParams();
  const language = translator.getLanguageByCountry(country);

  const renderIcons = () => {
    if (paymentIcons && paymentIcons.length > 0) {
      return paymentIcons.map((icon) => (
        <div key={icon.code} className="w-[97px] h-[50px] flex items-center justify-center bg-[#F9F9FB] xl:bg-white rounded-[12px]">
          <img
            src={icon.iconUrl}
            alt={icon.title}
            className="max-w-[85%] max-h-[85%] object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      ));
    }

    const staticIcons = STATIC_ICONS_BY_COUNTRY[(country || '').toLowerCase()] ?? STATIC_ICONS;

    return staticIcons.map((icon) => (
      <div key={icon.alt} className="w-[97px] h-[50px] flex items-center justify-center bg-[#F9F9FB] xl:bg-white rounded-[12px]">
        <img src={icon.src} alt={icon.alt} className="max-w-[85%] max-h-[85%] object-contain" />
      </div>
    ));
  };

  return (
    <div
      id="accepted-payment-methods-banner-wrapper"
      className="flex justify-center xl:mx-0 xl:px-[25px]"
    >
      <div
        id="accepted-payment-methods-banner"
        className="xl:bg-[#F5F6F7] rounded-[16px] xl:px-[31px] py-[21px] grid grid-flow-row xl:grid-flow-col items-center lg:w-fit"
      >
        <div
          id="accepted-payment-methods-banner-wallet-icon"
          className="hidden xl:flex w-[40px] bg-white aspect-square rounded-[6px] items-center justify-center shadow-[0_12px_18px_0_#CED0D8B3,0_-12px_16px_0_#FFFFFFD1]"
          style={{
            border: '1px solid #FFFFFF',
          }}
        >
          <img src={wallet} alt="wallet" />
        </div>

        <span
          id="accepted-payment-methods-banner-text"
          className="text-[14px] xl:text-[18px] text-[#212121] text-center xl:text-left font-semibold xl:font-[400] font-poppins leading-[1] xl:mr-[44px] xl:ml-[16px] mb-[24px] xl:mb-[0px]"
        >
          {translator.translate(language, "default", "AcceptedPaymentMethodsBanner/text")}
        </span>

        <div className="flex items-center xl:gap-[40px] gap-[16px] max-w-[350px] lg:max-w-none flex-wrap justify-center">
          {renderIcons()}
        </div>
      </div>
    </div>
  );
}
